import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import MqttManager from "../mqtt/Base";
import { Context } from "./Context";

export const MqttContext = createContext();
export const useMqtt = () => useContext(MqttContext);

export const MqttProvider = ({ children }) => {
  const [clients, setClients] = useState({});
  const clientsRef = useRef({});
  const { authUser, devices, setRefreshMqtt, setIsLoading } = useContext(Context);
  const mqttManagerRef = useRef(new MqttManager());
  const disconnectTimerRef = useRef(null);

  // Flag to indicate whether clients have been fully disconnected.
  // Start with true so the initial mount will attempt to connect.
  const isDisconnectedRef = useRef(true);

  // Prevent re-entrancy while a connect process is ongoing.
  const isConnectingRef = useRef(false);

  const disconnectAllClients = () => {
    try {
      Object.entries(clientsRef.current).forEach(([deviceId, client]) => {
        if (client) {
          try {
            // end connection immediately and force cleanup
            client.end(true);
          } catch (err) {
            // swallow per-client errors but keep going
            // console.error(`Error ending client ${deviceId}:`, err);
          }
        }
      });
    } finally {
      clientsRef.current = {};
      setClients({});
      isDisconnectedRef.current = true; // mark disconnected
      isConnectingRef.current = false;
    }
  };

  const connectAllClients = () => {
    if (!authUser) return;

    // If we're already in a connect process, don't start another.
    if (isConnectingRef.current) return;

    // If clients are still considered connected, do not reconnect (per requirement A).
    if (isDisconnectedRef.current === false) {
      return;
    }

    isConnectingRef.current = true;
    // Optionally toggle refresh flag if provided by Context (keep existing behavior)
    try {
      if (typeof setRefreshMqtt === "function") {
        setRefreshMqtt((prev) => !prev);
      }
    } catch (err) {
      // ignore if Context doesn't provide it or it throws
    }

    const newClients = {};

    try {
      devices.forEach((device) => {
        const existingClient = clientsRef.current[device.id];

        // skip bus devices or already-active clients
        if (
          device.is_bus == 1 ||
          (existingClient &&
            (existingClient.connected || existingClient.reconnecting))
        ) {
          return;
        }

        // if existing client is in a disconnected state, ensure it's cleaned up
        if (
          existingClient &&
          (existingClient.disconnected || existingClient.disconnecting)
        ) {
          try {
            existingClient.end(true);
          } catch (err) {
            // ignore
          }
          delete clientsRef.current[device.id];
        }

        // create a new client instance via mqtt manager
        const client = mqttManagerRef.current.connect(
          device.id,
          `wss://${device.url}:8080`,
          {
            username: device.username,
            password: device.password,
          }
        );

        // subscribe on connect
        client.on("connect", () => {
          try {
            mqttManagerRef.current.subscribe(device.id, `${device.serial}/app`);
          } catch (err) {
            // console.error("Subscribe error:", err);
          }
        });

        // basic error handlers to avoid silent failures
        client.on("error", (err) => {
          // console.error(`MQTT error for device ${device.id}:`, err);
        });

        client.on("close", () => {
          // When an individual client closes, we don't assume full disconnect of all clients.
          // A full disconnect only occurs via disconnectAllClients() path (visibility timer).
        });

        newClients[device.id] = client;
      });

      if (Object.keys(newClients).length > 0) {
        clientsRef.current = { ...clientsRef.current, ...newClients };
        setClients({ ...clientsRef.current });
      }

      // mark as connected (or at least connect attempt made)
      isDisconnectedRef.current = false;
    } finally {
      isConnectingRef.current = false;
    }
  };

  // Connect initially if visible and authUser present
  useEffect(() => {
    if (authUser && document.visibilityState === "visible") {
      connectAllClients();
    }
    // cleanup on unmount: ensure connections are torn down
    return () => {
      // clear any pending timer and disconnect clients
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      disconnectAllClients();
    };
    // Intentionally include only authUser and devices dependencies needed for lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, devices]);

  // Visibility handling: start a 2-minute timer on hidden, cancel on visible.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // start / restart the 2-minute disconnect timer
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
        }
        disconnectTimerRef.current = setTimeout(() => {
          disconnectAllClients();
        }, 0.5 * 60 * 1000);
      } else if (document.visibilityState === "visible") {
        // cancel pending disconnect if returning early
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }

        // Only reconnect if we previously fully disconnected (requirement A).
        if (isDisconnectedRef.current === true) {
          connectAllClients();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, devices]);

  return (
    <MqttContext.Provider value={{ clients, setClients }}>
      {children}
    </MqttContext.Provider>
  );
};
